import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { Button } from '@components/Button';
import { Icons } from '@components/Icons/Icons';
import { TooltipWrapper } from '@components/Tooltip/Tooltip';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { useCustomer } from '@hooks/customer/useCustomer';
import { useLogout } from '@hooks/customer/useLogout';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { LoginModalUncontrolled } from './LoginModal';

const ICON_SIZE = '1.50rem';

function LoggedOut() {
  return (
    <LoginModalUncontrolled
      trigger={
        <Button
          aria-label="Log in"
          className="relative w-10 rounded-full ring-grey-mid ring-offset-2 
          hover:scale-110 hover:bg-white hover:text-black hover:ring-1 hover:ring-grey-mid dark:hover:bg-grey-dark dark:hover:text-white
          active:scale-110 active:bg-white active:text-black active:ring-1 active:ring-grey-mid dark:active:bg-grey-dark dark:active:text-white"
          buttonStyle="tertiary"
          icon={<Icons.User.NoAuth size="1.75rem" />}
        />
      }
    />
  );
}

const itemActionClasses = 'px-6 h-12 leading-12 !flex gap-3 items-center';

function LoggedIn() {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);

  const logout = useLogout();
  const router = useRouter();

  const itemClasses =
    'select-none outline-hidden data-highlighted:bg-orange data-highlighted:text-white active:bg-orange active:text-white flex items-center justify-between rounded-2xl';

  // close the quick cart on route change
  useEffect(() => {
    const handleRouteChange = () => {
      setOpen(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      <ErrorAlert show={error} setShow={setError}>
        <Paragraph>
          There was an issue logging you out. Please try again
        </Paragraph>
      </ErrorAlert>
      <DropdownMenu.Root open={open} onOpenChange={(state) => setOpen(state)}>
        <TooltipWrapper text="Your Account">
          <DropdownMenu.Trigger asChild>
            <Button
              aria-label="Your account"
              className="relative w-10 h-10 rounded-full ring-grey-mid ring-offset-2 
                hover:scale-110 hover:bg-white hover:text-black hover:ring-1 hover:ring-grey-mid dark:hover:bg-grey-dark dark:hover:text-white
                active:scale-110 active:bg-white active:text-black active:ring-1 active:ring-grey-mid dark:active:bg-grey-dark dark:active:text-white"
              buttonStyle="tertiary"
              icon={<Icons.User.Auth size="2rem" />}
            />
          </DropdownMenu.Trigger>
        </TooltipWrapper>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="z-3 min-w-[220px] bg-white p-1 shadow-tooltip will-change-[opacity,transform] data-[side=bottom]:animate-slide-up-and-fade data-[side=left]:animate-slide-right-and-fade data-[side=right]:animate-slide-left-and-fade data-[side=top]:animate-slide-down-and-fade dark:bg-grey-darkest dark:shadow-tooltip-dark [&_a]:inline-block [&_a]:w-full rounded-2xl"
            sideOffset={5}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/account/home"
                aria-label="Your account home"
                className={itemActionClasses}
              >
                <Icons.Home size={ICON_SIZE} /> Account Home
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/account/reviews"
                aria-label="Your reviews"
                className={itemActionClasses}
              >
                <Icons.Rewards.WriteReview size={ICON_SIZE} /> Reviews
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/account/rewards"
                aria-label="Your rewards"
                className={itemActionClasses}
              >
                <Icons.Trophy.Outline size={ICON_SIZE} /> Rewards
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/account/favourites"
                aria-label="Your favourite products"
                className={itemActionClasses}
              >
                <Icons.Heart.Outline size={ICON_SIZE} /> Favourites
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/account/orders"
                aria-label="Previous orders"
                className={itemActionClasses}
              >
                <Icons.History size={ICON_SIZE} /> Previous Orders
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/account/addresses"
                aria-label="Saved addresses"
                className={itemActionClasses}
              >
                <Icons.AddressCard size={ICON_SIZE} /> Saved Addresses
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/account/settings"
                aria-label="Login & Security"
                className={itemActionClasses}
              >
                <Icons.Settings size={ICON_SIZE} /> Login & Security
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/account/referral-program"
                aria-label="Referral program"
                className={itemActionClasses}
              >
                <Icons.CS.Chat size={ICON_SIZE} /> Referral Program
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/account/preferences"
                aria-label="Communication Preferences"
                className={itemActionClasses}
              >
                <Icons.Social.Email size={ICON_SIZE} /> Communication
                Preferences
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClasses}>
              <Link
                href="/contact-us"
                aria-label="Customer Service & FAQs"
                className={itemActionClasses}
              >
                <Icons.CS.Phone size={ICON_SIZE} /> Customer Service & FAQs
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
            <DropdownMenu.Item className={itemClasses}>
              <button
                type="button"
                onClick={handleLogout}
                className={itemActionClasses}
              >
                <Icons.User.NoAuth size={ICON_SIZE} /> Log out
              </button>
            </DropdownMenu.Item>
            <DropdownMenu.Arrow className="fill-white dark:fill-grey-darkest" />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
}

export function DropdownLogin() {
  const { customer } = useCustomer();

  return customer ? <LoggedIn /> : <LoggedOut />;
}
